import {
  CheckEligibiltyIcon,
  CustomButton,
  Modal,
  SvgVerticalSplitter,
  SvgWarningNew,
  Tooltip,
} from '@ui-kit'
import { useEffect, useState, useImperativeHandle, forwardRef } from 'react'
import {
  ROWS_TAB_DIFFERENCE_FOR_LAYOUT_RENDER_TABS,
  TAB_INDEX,
} from '../../constants/form.constants'
import { SelectProgramProduct } from './SelectProgramProduct'
import {
  CHECK_ELIGIBILITY_LOCATION,
  extractProductName,
  fetchInitialData,
  handleMaxRTE,
  ifEligibleToRunMaxRTE,
  MAX_RTE_NAME_SPACE,
} from '../../utils/maxRTE'
import { store, useAppSelector } from '../../utils/agent.store'
import {
  addToaster,
  DataTypeStore,
  getLayoutByEntityNameAndTypeApi,
  hideLoader,
  LayoutResponseType,
  shouldCallMaxRTE,
  showLoader,
  updateMaxRTEShouldAutoScroll,
  useDispatch,
} from '@app-store'
import {
  ENTITY_NAME,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
} from '../../utils/constants'
import { EntityLayout, LayoutData, Page } from '../../types/layout.types'
import {
  generateTabIndexMap,
  getMaxTabIndexValue,
} from '../../utils/tab-indexing'
import FormRenderer from '../FormRenderer'
import { v4 as uuid } from 'uuid'

import {
  editEntityData,
  getEntityDataByEntityNameAndLayout,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { TabIndexMap } from '../../types/common'
import { useParams } from 'react-router-dom'
import { CheckEligibilityMaxRTEPropsType } from '../../types/maxRTE.types'
import { AddDataModal } from '../MultiLookupRowRender'
import { FieldValues } from 'react-hook-form'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'
import { MAX_RTE_STRINGS } from '../../utils/strings'
import { IMaxRTEButton } from '../../types/workflow.types'

const CheckEligibilityMaxRTE = forwardRef<
  IMaxRTEButton,
  CheckEligibilityMaxRTEPropsType
>(
  (
    {
      patientId,
      showDivider,
      formMethodsRef,
      from,
      getValues,
      showTextButton = false,
    },
    ref
  ) => {
    const [openProductModal, setOpenProductModal] = useState(false)
    const [editLayout, setEditLayout] = useState<LayoutResponseType>()
    const showError = (message: string) =>
      store.dispatch(addToaster({ id: uuid(), message, type: 'error' }))

    const workflowDefinition = useAppSelector(
      (state) => state?.workflowData?.workflowDefinition
    )
    const { caseId } = useParams()
    const [tabIndexMapForAddModal, setTabIndexMapForAddModal] =
      useState<TabIndexMap>({})
    const [dataValue, setDataValue] = useState<DataTypeStore | null>(null)
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false)
    const [modal, setModal] = useState<{
      title?: string
      message: string
      state: boolean
      onYesClick?: () => void
    }>({
      message: '',
      state: false,
    })
    const dispatch = useDispatch()

    const fetchEditLayout = async (patientId: number) => {
      try {
        dispatch(showLoader())
        const entityName = ENTITY_NAME.PATIENTS

        const response = await getLayoutByEntityNameAndTypeApi({
          entityName,
          type: LAYOUT_TYPE_NAMESPACE.VIEW_EDIT_TYPE,
          isDefault: true,
        })
        setEditLayout(response?.data?.[0])
        const layoutId = response?.data?.[0]?.id

        const viewEntityData = await getEntityDataByEntityNameAndLayout(
          entityName,
          Number(layoutId),
          patientId
        )
        setDataValue(viewEntityData?.data)
        setIsAddModalOpen(true)
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }
    const runMaxRTEFlow = async (
      patientId: number | null,
      productName: string | null
    ) => {
      const proceedFlow = ifEligibleToRunMaxRTE(patientId, productName)
      if (!proceedFlow) return false

      const maxRTESuccessful = await handleMaxRTE(
        patientId ?? 0,
        productName ?? ''
      )

      await fetchEditLayout(Number(patientId))

      dispatch(updateMaxRTEShouldAutoScroll(true))
      return maxRTESuccessful
    }
    const handleCheckEligibility = async () => {
      try {
        let patientIdVal: number | null = null
        let productName: string | null = null
        let productId
        switch (from) {
          case CHECK_ELIGIBILITY_LOCATION.ENTITIES:
            patientIdVal = Number(patientId)
            break

          case CHECK_ELIGIBILITY_LOCATION.INTAKE: {
            const formData = formMethodsRef?.current?.getValues()
            productName = extractProductName(formData?.lookupLabels?.products)
            productId = formData?.products?.id
            patientIdVal = formData?.patient?.id
            break
          }
          case CHECK_ELIGIBILITY_LOCATION.CASE: {
            const response = await getEntityDataByIdEmbedded(
              workflowDefinition?.global_variables?.entity_name ?? '',
              Number(workflowDefinition?.global_variables?.entity_id),
              ['patient', 'products'],
              Number(caseId)
            )
            if (response?.data) {
              productName = extractProductName(
                response?.data?.products?.trademarked_name
              )
              productId = response?.data?.products?.id
              patientIdVal = response?.data?.patient?.id
            }
          }
        }
        if (!patientIdVal) {
          dispatch(
            addToaster({
              id: uuid(),
              message: MAX_RTE_STRINGS.PATIENT_REQUIRED,
              type: 'error',
            })
          )
          return
        }
        const checkshouldCallMaxRTE = await shouldCallMaxRTE(
          patientIdVal,
          productId,
          caseId
        )
        if (checkshouldCallMaxRTE?.data?.isValid) {
          if (from === CHECK_ELIGIBILITY_LOCATION.ENTITIES) {
            setOpenProductModal(true)
          } else if (checkshouldCallMaxRTE?.data?.consentsVerified) {
            await runMaxRTEFlow(patientIdVal, productName)
          } else {
            await new Promise(() => {
              showError(
                workflowDefinition?.name?.includes('PAP')
                  ? MAX_RTE_STRINGS.HIPAA_CONSENT_MISSING_CASE
                  : MAX_RTE_STRINGS.HIPAA_CONSENT_MISSING_PRODUCT
              )
            })
          }
        } else {
          await new Promise((resolve) => {
            setModal({
              title:
                checkshouldCallMaxRTE?.data?.title ??
                MAX_RTE_STRINGS.VALID_RECORD_EXIST_TITLE,
              message:
                checkshouldCallMaxRTE?.data?.message ??
                MAX_RTE_STRINGS.VALID_RECORD_EXIST,
              state: true,
              onYesClick: () => {
                resolve(true)
              },
            })
          })
          await fetchInitialData(patientIdVal)
          if (
            from === CHECK_ELIGIBILITY_LOCATION.INTAKE ||
            from === CHECK_ELIGIBILITY_LOCATION.CASE
          )
            await fetchEditLayout(Number(patientIdVal))
          dispatch(updateMaxRTEShouldAutoScroll(true))
        }
      } catch (e) {
        console.error(e)
      }
    }

    useImperativeHandle(ref, () => ({
      triggerCheckEligibility: handleCheckEligibility,
    }))

    const toggleAddModal = () => {
      setIsAddModalOpen((prev) => !prev)
    }
    const handleSubmit = async (values: FieldValues, embedded: string[]) => {
      try {
        const res = await editEntityData(
          editLayout?.entity_name ?? '',
          editLayout?.entity_id ?? 0,
          editLayout?.id ?? 0,
          values,
          (dataValue?.id as number) ?? 0,
          embedded
        )
        if (res) {
          dispatch(
            addToaster({
              message: TOASTER_NAMESPACE.EDIT_SUCCESS,
              type: 'success',
              id: uuid(),
            })
          )

          setIsAddModalOpen((prev) => !prev)
        }
      } catch (e) {
        console.log(e)
      }
    }
    useEffect(() => {
      if (isAddModalOpen) {
        setTabIndexMapForAddModal(
          generateTabIndexMap(
            (editLayout?.layout_data?.pages as unknown as Page[]) ?? [],
            ROWS_TAB_DIFFERENCE_FOR_LAYOUT_RENDER_TABS
          )
        )
      }
    }, [editLayout?.layout_data, isAddModalOpen])
    return (
      <div className="flex relative justify-center items-center">
        <Tooltip content={MAX_RTE_NAME_SPACE.CHECK_ELIGIBILITY_TITLE}>
          {showTextButton ? (
            <CustomButton tabIndex={TAB_INDEX} onClick={handleCheckEligibility}>
              {MAX_RTE_NAME_SPACE.CHECK_ELIGIBILITY_TITLE}
            </CustomButton>
          ) : (
            <CheckEligibiltyIcon
              tabIndex={TAB_INDEX}
              onClick={handleCheckEligibility}
              className="cursor-pointer"
            />
          )}
        </Tooltip>
        {openProductModal &&
          from === CHECK_ELIGIBILITY_LOCATION.ENTITIES &&
          getValues && (
            <Modal
              isOpen={openProductModal}
              closeModal={() => setOpenProductModal(false)}
              bodyCssClassName="min-w-[34.25rem] !rounded-[0] w-[34.25rem]"
              showCloseButton={false}
            >
              <SelectProgramProduct
                setOpenProductModal={setOpenProductModal}
                // getValues={getValues}
                patientId={Number(patientId)}
              />
            </Modal>
          )}
        {showDivider && <SvgVerticalSplitter className="ml-3" />}
        {isAddModalOpen && dataValue && editLayout && (
          <AddDataModal>
            <Modal
              isOpen={isAddModalOpen}
              closeModal={toggleAddModal}
              showCloseButton={false}
              width="min-w-[75vw] max-w-[60%]"
            >
              <FormRenderer
                heading={
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-base">
                      {NAMESPACES?.PATIENT} | {NAMESPACES?.EDIT}
                    </span>
                    <div className="flex gap-3">
                      <CustomButton
                        onClick={() => {
                          setIsAddModalOpen(false)
                        }}
                        tabIndex={getMaxTabIndexValue({
                          tabIndexMapForAddModal,
                        })}
                      >
                        <span>{NAMESPACES.CANCEL_BUTTON}</span>
                      </CustomButton>
                      <CustomButton
                        isFilled
                        type="submit"
                        tabIndex={
                          getMaxTabIndexValue({
                            tabIndexMapForAddModal,
                          }) + 1
                        }
                      >
                        <span>{NAMESPACES.SAVE_BUTTON}</span>
                      </CustomButton>
                    </div>
                  </div>
                }
                handleFormSubmit={handleSubmit}
                fullLayout={editLayout as EntityLayout}
                layout={editLayout?.layout_data as LayoutData}
                mode={'edit'}
                data={dataValue}
                tabIndexMap={tabIndexMapForAddModal}
              />
            </Modal>
          </AddDataModal>
        )}
        <Modal
          isOpen={modal.state}
          closeModal={() => {
            setModal({ message: '', title: '', state: false })
            if (modal.onYesClick) modal.onYesClick?.()
          }}
          showCloseButton={true}
          width="w-[38.5rem]"
        >
          <div className="flex items-center justify-center text-sm px-2 font-primary ">
            <div className="flex items-center justify-center gap-4 flex-col w-full">
              <div className="py-4 border-b w-full flex justify-center items-center flex-col gap-2">
                {modal.title && (
                  <div className="flex items-center gap-4 justify-center text-sm text-accent-700 font-bold">
                    <SvgWarningNew />
                    <span>{modal?.title}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 justify-center text-sm font-light text-accent-700 w-full">
                  <span>{modal?.message}</span>
                </div>
              </div>
              <div className="flex gap-3 items-center justify-end !w-full">
                <CustomButton
                  onClick={() => {
                    setModal({ message: '', title: '', state: false })
                    if (modal.onYesClick) modal.onYesClick?.()
                  }}
                  color="primary"
                  isFilled={true}
                  className="w-fit"
                >
                  OK
                </CustomButton>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    )
  }
)

CheckEligibilityMaxRTE.displayName = 'CheckEligibilityMaxRTE'

export default CheckEligibilityMaxRTE
